import React, { useEffect, useMemo, useState } from "react";
import {
  adminLogin,
  clearAdminToken,
  fetchAdminDashboard,
  getAdminToken,
  setAdminToken,
  updateAdminUser,
} from "../services/adminService";

const PAGE_SIZE = 20;

const styles = {
  wrapper: {
    maxWidth: 1200,
    margin: "28px auto",
    padding: "0 16px 52px",
    color: "#1f2937",
  },
  loginCard: {
    maxWidth: 460,
    margin: "64px auto",
    padding: 28,
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
  },
  title: { margin: 0, fontSize: 30, fontWeight: 700 },
  subtitle: { marginTop: 8, color: "#4b5563" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  button: {
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    padding: "8px 12px",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 600,
  },
  primaryButton: {
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    padding: "8px 12px",
    background: "#2563eb",
    color: "white",
    cursor: "pointer",
    fontWeight: 600,
  },
  section: {
    marginTop: 22,
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 18,
    background: "#ffffff",
    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.05)",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  statCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#f8fafc",
  },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "9px 11px",
    width: "100%",
    boxSizing: "border-box",
  },
  tableWrap: { overflowX: "auto", marginTop: 10 },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#334155",
    background: "#f8fafc",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  td: { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  pager: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, alignItems: "center" },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 560,
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 40px rgba(2, 6, 23, 0.2)",
    padding: 18,
  },
};

const paginate = (items, page) => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

function AdminMyRoomPage() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [creditInput, setCreditInput] = useState({});
  const [usersPage, setUsersPage] = useState(1);
  const [companiesPage, setCompaniesPage] = useState(1);
  const [analysesPage, setAnalysesPage] = useState(1);

  const [emailModalUser, setEmailModalUser] = useState(null);
  const [modalEmail, setModalEmail] = useState("");
  const [modalPhrase, setModalPhrase] = useState("");
  const [modalConfirmLogin, setModalConfirmLogin] = useState("");
  const [modalConfirmPassword, setModalConfirmPassword] = useState("");

  const isLoggedIn = Boolean(getAdminToken());

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const json = await fetchAdminDashboard();
      setDashboard(json);
    } catch (e) {
      setError(e.message);
      if (e.message.toLowerCase().includes("token")) {
        clearAdminToken();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadDashboard();
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await adminLogin({ login, password });
      setAdminToken(result.token);
      await loadDashboard();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const users = useMemo(() => dashboard?.users || [], [dashboard]);
  const cachedCompanies = useMemo(() => dashboard?.cached_companies || [], [dashboard]);
  const analyses = useMemo(() => dashboard?.analysis_history || [], [dashboard]);

  const usersPageCount = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  const companiesPageCount = Math.max(1, Math.ceil(cachedCompanies.length / PAGE_SIZE));
  const analysesPageCount = Math.max(1, Math.ceil(analyses.length / PAGE_SIZE));

  const usersRows = useMemo(() => paginate(users, usersPage), [users, usersPage]);
  const companyRows = useMemo(() => paginate(cachedCompanies, companiesPage), [cachedCompanies, companiesPage]);
  const analysesRows = useMemo(() => paginate(analyses, analysesPage), [analyses, analysesPage]);

  useEffect(() => {
    setUsersPage((prev) => Math.min(prev, usersPageCount));
  }, [usersPageCount]);

  useEffect(() => {
    setCompaniesPage((prev) => Math.min(prev, companiesPageCount));
  }, [companiesPageCount]);

  useEffect(() => {
    setAnalysesPage((prev) => Math.min(prev, analysesPageCount));
  }, [analysesPageCount]);

  const applyCredits = async (userId) => {
    const amount = Number(creditInput[userId] || 0);
    if (!amount || amount < 1) return;
    try {
      await updateAdminUser({ userId, creditsDelta: amount });
      await loadDashboard();
      setCreditInput((prev) => ({ ...prev, [userId]: "" }));
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleBlock = async (userId, blocked) => {
    try {
      await updateAdminUser({ userId, blocked: !blocked });
      await loadDashboard();
    } catch (e) {
      setError(e.message);
    }
  };

  const openEmailModal = (user) => {
    setEmailModalUser(user);
    setModalEmail(user?.email || "");
    setModalPhrase("");
    setModalConfirmLogin("");
    setModalConfirmPassword("");
  };

  const closeEmailModal = () => {
    setEmailModalUser(null);
  };

  const applyEmailFromModal = async () => {
    if (!emailModalUser) return;
    const email = modalEmail.trim();
    if (!email) return;
    if (modalPhrase.trim() !== "POTVRZUJI") {
      setError("Pro změnu e-mailu musíte napsat potvrzení POTVRZUJI.");
      return;
    }

    try {
      await updateAdminUser({
        userId: emailModalUser.id,
        email,
        confirmLogin: modalConfirmLogin,
        confirmPassword: modalConfirmPassword,
      });
      await loadDashboard();
      closeEmailModal();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!isLoggedIn) {
    return (
      <div style={styles.loginCard}>
        <h1 style={{ margin: 0 }}>MyRoom administrace</h1>
        <p style={styles.subtitle}>Přístup je povolen pouze pro účet z ENV konfigurace.</p>
        <form onSubmit={handleLogin} style={{ display: "grid", gap: 12 }}>
          <input style={styles.input} placeholder="Admin login" value={login} onChange={(e) => setLogin(e.target.value)} />
          <input
            style={styles.input}
            placeholder="Admin heslo"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit" style={styles.primaryButton} disabled={loading}>{loading ? "Přihlašuji..." : "Přihlásit"}</button>
          {error && <p style={{ color: "#b91c1c", margin: 0 }}>{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>/myroom — Administrace</h1>
          <p style={styles.subtitle}>Správa uživatelů, kreditů, analýz a přístupových statistik.</p>
        </div>
        <button style={styles.button} onClick={() => { clearAdminToken(); window.location.reload(); }}>Odhlásit</button>
      </div>

      {error && <p style={{ color: "#b91c1c", marginTop: 14 }}>{error}</p>}
      {loading && <p style={{ marginTop: 14 }}>Načítání dat…</p>}

      <section style={styles.section}>
        <h2 style={{ marginTop: 0 }}>Přehled</h2>
        <div style={styles.statGrid}>
          <div style={styles.statCard}><strong>Uživatelé:</strong><div>{users.length}</div></div>
          <div style={styles.statCard}><strong>Analýzy firem:</strong><div>{dashboard?.analysis_history?.length || 0}</div></div>
          <div style={styles.statCard}><strong>Finanční analýzy:</strong><div>{dashboard?.finanalyst_history?.length || 0}</div></div>
          <div style={styles.statCard}><strong>Záznamy přihlášení:</strong><div>{dashboard?.login_stats?.total_events || 0}</div></div>
          <div style={styles.statCard}><strong>Firmy v cache:</strong><div>{cachedCompanies.length}</div></div>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={{ marginTop: 0 }}>1) Uživatelé, kredity a blokace</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Uživatel</th>
                <th style={styles.th}>Kredity</th>
                <th style={styles.th}>Stav</th>
                <th style={styles.th}>Správa</th>
              </tr>
            </thead>
            <tbody>
              {usersRows.map((user) => (
                <tr key={user.id}>
                  <td style={styles.td}>{user.id}</td>
                  <td style={styles.td}>
                    <div><strong>{user.name}</strong></div>
                    <div style={{ color: "#475569", fontSize: 14 }}>{user.email}</div>
                  </td>
                  <td style={styles.td}>{user.credits}</td>
                  <td style={styles.td}>{user.is_blocked ? "Blokovaný" : "Aktivní"}</td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: 6, minWidth: 280, flexWrap: "wrap" }}>
                      <input
                        style={{ ...styles.input, maxWidth: 110 }}
                        type="number"
                        min="1"
                        placeholder="+kredity"
                        value={creditInput[user.id] || ""}
                        onChange={(e) => setCreditInput((prev) => ({ ...prev, [user.id]: e.target.value }))}
                      />
                      <button style={styles.button} onClick={() => applyCredits(user.id)}>Přidat kredity</button>
                      <button style={styles.button} onClick={() => toggleBlock(user.id, user.is_blocked)}>
                        {user.is_blocked ? "Odblokovat" : "Zablokovat"}
                      </button>
                      <button style={styles.primaryButton} onClick={() => openEmailModal(user)}>Změnit e-mail</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={styles.pager}>
          <button style={styles.button} onClick={() => setUsersPage((p) => Math.max(1, p - 1))} disabled={usersPage === 1}>Předchozí</button>
          <span>Strana {usersPage} / {usersPageCount}</span>
          <button style={styles.button} onClick={() => setUsersPage((p) => Math.min(usersPageCount, p + 1))} disabled={usersPage === usersPageCount}>Další</button>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={{ marginTop: 0 }}>2) Seznam firem uložených v cache</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Firma</th>
                <th style={styles.th}>IČO</th>
                <th style={styles.th}>Obor ID</th>
                <th style={styles.th}>Aktualizováno</th>
              </tr>
            </thead>
            <tbody>
              {companyRows.map((company) => (
                <tr key={company.id}>
                  <td style={styles.td}>{company.company_name || "Neznámý název"}</td>
                  <td style={styles.td}>{company.ico}</td>
                  <td style={styles.td}>{company.raw_obor_id || "—"}</td>
                  <td style={styles.td}>{company.updated_at || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={styles.pager}>
          <button style={styles.button} onClick={() => setCompaniesPage((p) => Math.max(1, p - 1))} disabled={companiesPage === 1}>Předchozí</button>
          <span>Strana {companiesPage} / {companiesPageCount}</span>
          <button style={styles.button} onClick={() => setCompaniesPage((p) => Math.min(companiesPageCount, p + 1))} disabled={companiesPage === companiesPageCount}>Další</button>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={{ marginTop: 0 }}>2b) Seznam provedených analýz</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Firma</th>
                <th style={styles.th}>IČO</th>
                <th style={styles.th}>Stav</th>
                <th style={styles.th}>Vytvořeno</th>
              </tr>
            </thead>
            <tbody>
              {analysesRows.map((item) => (
                <tr key={item.id}>
                  <td style={styles.td}>{item.company_name || "Neznámá firma"}</td>
                  <td style={styles.td}>{item.ico || "—"}</td>
                  <td style={styles.td}>{item.status || "—"}</td>
                  <td style={styles.td}>{item.created_at || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.pager}>
          <button style={styles.button} onClick={() => setAnalysesPage((p) => Math.max(1, p - 1))} disabled={analysesPage === 1}>Předchozí</button>
          <span>Strana {analysesPage} / {analysesPageCount}</span>
          <button style={styles.button} onClick={() => setAnalysesPage((p) => Math.min(analysesPageCount, p + 1))} disabled={analysesPage === analysesPageCount}>Další</button>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={{ marginTop: 0 }}>3) Statistiky přihlášení uživatelských účtů</h2>
        <p style={{ color: "#475569" }}>
          Celkem záznamů (posledních 200): <strong>{dashboard?.login_stats?.total_events || 0}</strong>
        </p>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID uživatele</th>
                <th style={styles.th}>Počet přihlášení</th>
              </tr>
            </thead>
            <tbody>
              {(dashboard?.login_stats?.per_user || []).map((item) => (
                <tr key={item.user_id}>
                  <td style={styles.td}>{item.user_id}</td>
                  <td style={styles.td}>{item.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {emailModalUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <h3 style={{ marginTop: 0 }}>Změna e-mailu uživatele</h3>
            <p style={{ color: "#475569", marginTop: 0 }}>
              Uživatel: <strong>{emailModalUser.name}</strong> (ID: {emailModalUser.id})
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              <input style={styles.input} placeholder="Nový e-mail" value={modalEmail} onChange={(e) => setModalEmail(e.target.value)} />
              <input style={styles.input} placeholder="Potvrďte text: POTVRZUJI" value={modalPhrase} onChange={(e) => setModalPhrase(e.target.value)} />
              <input style={styles.input} placeholder="Váš admin login" value={modalConfirmLogin} onChange={(e) => setModalConfirmLogin(e.target.value)} />
              <input style={styles.input} type="password" placeholder="Vaše admin heslo" value={modalConfirmPassword} onChange={(e) => setModalConfirmPassword(e.target.value)} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button style={styles.button} onClick={closeEmailModal}>Zrušit</button>
              <button style={styles.primaryButton} onClick={applyEmailFromModal}>Uložit změnu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMyRoomPage;
